import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactComponent as CloseIcon } from '../../icons/close.svg';
import api from '../../api';
import type { TFACodeRequestEvent, TFAType } from '@dreamai/types';
import './tfa-code-input.scss';

interface TFACodeInputProps {
  request: TFACodeRequestEvent | null;
  onDismiss: () => void;
}

// `card_3ds` is intentionally included even if the currently-installed
// @dreamai/types build doesn't list it on TFAType yet — the backend never
// delivers `card_3ds` to the user (see `/request-mfa` and `requestUserCode`
// guards) but we keep a defensive label here in case it ever leaks.
const TYPE_LABELS: Record<string, string> = {
  sms_code: 'SMS verification',
  authenticator_code: 'Authenticator code',
  push_approval: 'Approve sign-in',
  email_code: 'Email verification',
  card_3ds: 'Card verification',
  cvv_entry: 'Card security code',
  other: 'Verification code',
};

const labelForType = (type: TFAType): string =>
  TYPE_LABELS[type as string] || 'Verification code';

const TFACodeInput: React.FC<TFACodeInputProps> = ({ request, onDismiss }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<
    'active' | 'submitting' | 'submitted' | 'expired' | 'error'
  >('active');
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when request changes
  useEffect(() => {
    if (request) {
      setCode('');
      setError(null);

      // Check if already expired
      if (Date.now() >= request.expiresAt) {
        setStatus('expired');
      } else {
        setStatus('active');
      }
    }
  }, [request?.requestId]);

  // Countdown timer
  useEffect(() => {
    if (!request || status !== 'active') return;

    const updateTimer = () => {
      const remaining = Math.max(0, request.expiresAt - Date.now());
      setTimeLeft(remaining);
      if (remaining === 0) {
        setStatus('expired');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [request, status]);

  // Auto-focus input when active
  useEffect(() => {
    if (status === 'active' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [status, request?.requestId]);

  const handleSubmit = useCallback(async () => {
    if (!request || !code.trim()) return;

    setStatus('submitting');
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/browser-agent/tfa-response',
        { requestId: request.requestId, code: code.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setStatus('submitted');
        setTimeout(() => onDismiss(), 2000);
      } else {
        setError('Failed to submit code');
        setStatus('active');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to submit code';
      if (err?.response?.status === 410) {
        setStatus('expired');
      } else {
        setError(msg);
        setStatus('active');
      }
    }
  }, [request, code, getAccessTokenSilently, onDismiss]);

  const handlePushApprovalDone = useCallback(async () => {
    if (!request) return;

    setStatus('submitting');
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/browser-agent/tfa-response',
        { requestId: request.requestId, code: '__push_approved__' },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setStatus('submitted');
        setTimeout(() => onDismiss(), 2000);
      } else {
        setError('Failed to confirm');
        setStatus('active');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to confirm';
      if (err?.response?.status === 410) {
        setStatus('expired');
      } else {
        setError(msg);
        setStatus('active');
      }
    }
  }, [request, getAccessTokenSilently, onDismiss]);

  /**
   * User-initiated dismissal while the request is still pending.
   * Notifies the backend so the assistant / operator hears about it
   * immediately instead of waiting for the ~10 minute expiry job.
   * For already-resolved states (submitted / expired) we just close
   * the modal — no server call needed.
   */
  const handleUserDismiss = useCallback(() => {
    if (request && (status === 'active' || status === 'submitting')) {
      // Fire-and-forget so the modal closes instantly. The backend
      // dispatch is idempotent and tolerant of races.
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          await api.post(
            '/browser-agent/tfa-dismiss',
            { requestId: request.requestId },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (err) {
          console.warn('[TFA] Dismiss notification failed:', err);
        }
      })();
    }
    onDismiss();
  }, [request, status, getAccessTokenSilently, onDismiss]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && code.trim()) {
        handleSubmit();
      }
    },
    [handleSubmit, code],
  );

  const formatTimeLeft = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!request) return null;

  const show = !!request;
  const isPushApproval = request.tfaType === 'push_approval';
  const isExpiringSoon = timeLeft < 60000 && timeLeft > 0;

  return (
    <Modal
      show={show}
      onHide={handleUserDismiss}
      centered
      dialogClassName='response-artifact-modal tfa-approval-modal'
      backdropClassName='tfa-approval-backdrop'
    >
      <Modal.Body>
        <div className='tfa-sheet'>
          <div className='tfa-sheet-header'>
            <h2>{labelForType(request.tfaType)}</h2>
            <button
              className='tfa-sheet-close-btn'
              onClick={handleUserDismiss}
              aria-label='Close'
            >
              <CloseIcon />
            </button>
          </div>

          <div className='tfa-sheet-body'>
            {status === 'expired' && (
              <div className='tfa-sheet-status tfa-sheet-status--expired'>
                <div className='tfa-sheet-status-title'>Request expired</div>
                <div className='tfa-sheet-status-message'>
                  This verification request has expired. Your secretary will
                  handle it.
                </div>
              </div>
            )}

            {status === 'submitted' && (
              <div className='tfa-sheet-status tfa-sheet-status--submitted'>
                <div className='tfa-sheet-status-title'>Code submitted</div>
                <div className='tfa-sheet-status-message'>
                  Your secretary is verifying the code now.
                </div>
              </div>
            )}

            {(status === 'active' || status === 'submitting') && (
              <>
                <div className='tfa-sheet-field'>
                  <label>Request</label>
                  <div className='tfa-sheet-value'>{request.message}</div>
                </div>

                {!isPushApproval && (
                  <div className='tfa-sheet-input-group'>
                    <input
                      ref={inputRef}
                      type='text'
                      inputMode='numeric'
                      autoComplete='one-time-code'
                      placeholder='Enter code'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={status === 'submitting'}
                      maxLength={12}
                    />
                  </div>
                )}

                {error && <div className='tfa-sheet-error'>{error}</div>}

                <div
                  className={`tfa-sheet-timer ${isExpiringSoon ? 'tfa-sheet-timer--expiring' : ''}`}
                >
                  Expires in {formatTimeLeft(timeLeft)}
                </div>
              </>
            )}
          </div>

          {(status === 'active' || status === 'submitting') && (
            <div className='tfa-sheet-footer'>
              <button
                className='tfa-sheet-cancel-btn'
                onClick={handleUserDismiss}
              >
                Dismiss
              </button>
              {isPushApproval ? (
                <button
                  className='tfa-sheet-approve-btn'
                  onClick={handlePushApprovalDone}
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting'
                    ? 'Confirming...'
                    : "I've approved it"}
                </button>
              ) : (
                <button
                  className='tfa-sheet-approve-btn'
                  onClick={handleSubmit}
                  disabled={!code.trim() || status === 'submitting'}
                >
                  {status === 'submitting' ? 'Submitting...' : 'Submit code'}
                </button>
              )}
            </div>
          )}

          {(status === 'expired' || status === 'submitted') && (
            <div className='tfa-sheet-footer'>
              <div />
              <button className='tfa-sheet-cancel-btn' onClick={onDismiss}>
                Close
              </button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TFACodeInput;
